import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtR, getToken, formatApiError, slugifyFilename } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import BranchMultiSelect from "@/components/BranchMultiSelect";
import { toast } from "sonner";
import { ArrowLeft, Download, Ban, Pencil, Plus, Trash2, Mail } from "lucide-react";

export default function VoucherDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [form, setForm] = useState(null);
    const [costShops, setCostShops] = useState([]);
    const [branches, setBranches] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailTplId, setEmailTplId] = useState("");
    const [sendingEmail, setSendingEmail] = useState(false);

    const load = async () => {
        const { data } = await api.get(`/admin/vouchers/${id}`);
        setData(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        api.get("/admin/cost-shops").then((r) => setCostShops(r.data));
        api.get("/admin/branches").then((r) => setBranches(r.data));
        api.get("/admin/email-templates").then((r) => setEmailTemplates(r.data.templates || []));
    }, []);

    useEffect(() => {
        if (!data) return;
        const token = getToken();
        fetch(`${api.defaults.baseURL}/admin/vouchers/${id}/qr`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then((r) => r.blob()).then((b) => setQrUrl(URL.createObjectURL(b)));
        return () => { if (qrUrl) URL.revokeObjectURL(qrUrl); };
        // eslint-disable-next-line
    }, [data, id]);

    const downloadQr = () => {
        if (!qrUrl) return;
        const a = document.createElement("a");
        a.href = qrUrl; a.download = `${slugifyFilename(data.voucher.name)}.png`; a.click();
    };

    const openEmail = () => {
        setEmailTo(data?.voucher?.issued_to_email || data?.voucher?.last_emailed_to || "");
        setEmailTplId("");
        setEmailOpen(true);
    };

    const sendEmail = async () => {
        if (!emailTo) return;
        setSendingEmail(true);
        try {
            const { data: r } = await api.post(`/admin/vouchers/${id}/email`, {
                to_email: emailTo,
                email_template_id: emailTplId || null,
            });
            toast.success(`Voucher emailed! Resend id: ${r.resend_id?.slice(0, 12)}…`);
            setEmailOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSendingEmail(false);
        }
    };


    const onVoid = async () => {
        if (!window.confirm("Void this voucher? This cannot be undone.")) return;
        try {
            await api.post(`/admin/vouchers/${id}/void`);
            toast.success("Voucher voided");
            load();
        } catch (e) { toast.error("Failed"); }
    };

    const openEdit = () => {
        const v = data.voucher;
        setForm({
            name: v.name,
            restrictions: v.restrictions || "",
            expiry_date: v.expiry_date ? v.expiry_date.slice(0, 10) : "",
            cost_shop_id: v.cost_shop_id || "none",
            allowed_branch_ids: v.allowed_branch_ids || [],
            type: v.type,
            value_amount: v.value_amount ?? 0,
            lines: v.type === "item"
                ? (v.lines || []).map((l) => ({ name: l.name, qty_per_use: l.qty_per_use, retail_value: l.retail_value }))
                : [],
        });
        setEditOpen(true);
    };

    const updateLine = (idx, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { name: "", qty_per_use: 1, retail_value: 0 }] }));
    const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

    const onSaveEdit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: form.name,
                restrictions: form.restrictions,
                expiry_date: form.expiry_date || null,
                cost_shop_id: form.cost_shop_id === "none" ? null : form.cost_shop_id,
                allowed_branch_ids: form.allowed_branch_ids,
            };
            if (form.type === "item") {
                const cleanLines = form.lines
                    .filter((l) => l.name && l.name.trim() !== "")
                    .map((l) => ({
                        name: l.name.trim(),
                        qty_per_use: parseInt(l.qty_per_use, 10) || 1,
                        retail_value: parseFloat(l.retail_value) || 0,
                    }));
                if (cleanLines.length === 0) { toast.error("Add at least one line item"); return; }
                payload.lines = cleanLines;
            } else {
                payload.value_amount = parseFloat(form.value_amount);
            }
            await api.patch(`/admin/vouchers/${id}`, payload);
            toast.success("Voucher updated");
            setEditOpen(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!data) return <div className="p-8 text-[#7A726D]">Loading…</div>;
    const v = data.voucher;
    // Voucher is editable while nothing has been redeemed yet.
    // For items: full pool remains on every line. For value: full value remains.
    const isEditable = v.status === "active" && (
        v.type === "value"
            ? Number(v.remaining_value) === Number(v.value_amount)
            : (v.lines || []).every(l => l.qty_remaining === l.qty_per_use)
    );
    const allowedBranchNames = (v.allowed_branch_ids || []).length === 0
        ? "All branches"
        : branches.filter((b) => (v.allowed_branch_ids || []).includes(b.id)).map((b) => b.name).join(", ") || `${v.allowed_branch_ids.length} branch(es)`;
    const costShopName = costShops.find((c) => c.id === v.cost_shop_id)?.name || "—";

    return (
        <div className="p-8 max-w-5xl" data-testid="voucher-detail-page">
            <Link to="/admin/vouchers" className="flex items-center gap-1 text-sm text-[#7A726D] mb-4" data-testid="back-to-vouchers">
                <ArrowLeft className="w-4 h-4" /> Back to vouchers
            </Link>

            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">{v.type === "item" ? "Bundle voucher" : "Value voucher"}</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight" data-testid="detail-name">{v.name}</h1>
                    <div className="mt-1 font-mono text-sm text-[#7A726D]" data-testid="detail-serial">{v.serial}</div>
                </div>
                <div className="flex items-center gap-3">
                    <StatusBadge status={v.status} />
                    <Button variant="outline" onClick={openEmail}
                        className="rounded-sm border-[#E5E0D8]" data-testid="email-voucher-button">
                        <Mail className="w-4 h-4 mr-1" /> Email voucher
                    </Button>
                    {isEditable ? (
                        <Button variant="outline" onClick={openEdit} className="rounded-sm border-[#E5E0D8]" data-testid="edit-voucher-button">
                            <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                    ) : v.status === "active" ? (
                        <div className="text-xs text-[#7A726D] italic max-w-[180px] text-right" data-testid="edit-locked-note">
                            Editing locked — already partially redeemed
                        </div>
                    ) : null}
                    {v.status === "active" && (
                        <Button variant="outline" onClick={onVoid} className="rounded-sm text-[#B24A45] border-[#B24A45]/30" data-testid="void-button">
                            <Ban className="w-4 h-4 mr-1" /> Void
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <Card className="border-[#E5E0D8] shadow-none rounded-sm lg:col-span-1">
                    <CardContent className="p-6 text-center">
                        {qrUrl ? (
                            <img src={qrUrl} alt={v.serial} className="w-full max-w-xs mx-auto rounded-sm border border-[#E5E0D8]" data-testid="qr-image"/>
                        ) : (
                            <div className="aspect-square bg-[#F3EFEA] animate-pulse rounded-sm" />
                        )}
                        <Button onClick={downloadQr} className="mt-4 w-full bg-espresso hover:bg-[#2c1a17] rounded-sm" data-testid="download-qr-button">
                            <Download className="w-4 h-4 mr-2" /> Download QR
                        </Button>
                    </CardContent>
                </Card>

                <Card className="border-[#E5E0D8] shadow-none rounded-sm lg:col-span-2">
                    <CardContent className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <Field label="Face value" value={`${fmtR(v.remaining_value)} of ${fmtR(v.value_amount)}`} />
                            {v.type === "item" ? (
                                <Field label="Items left" value={`${(v.lines || []).reduce((s, l) => s + l.qty_remaining, 0)} of ${(v.lines || []).reduce((s, l) => s + l.qty_per_use, 0)}`} />
                            ) : (
                                <Field label="Type" value="Rand value" />
                            )}
                            <Field label="Expiry" value={v.expiry_date ? v.expiry_date.slice(0, 10) : "No expiry"} />
                            <Field label="Cost shop" value={costShopName} />
                            <Field label="Created" value={v.created_at?.slice(0, 10)} />
                            <Field label="Redeemable at" value={allowedBranchNames} />
                        </div>

                        {v.type === "item" && (v.lines || []).length > 0 && (
                            <div className="pt-3 border-t border-[#E5E0D8]" data-testid="lines-summary">
                                <div className="text-xs text-[#7A726D] uppercase mb-2">Line items</div>
                                <div className="space-y-1.5">
                                    {(v.lines || []).map((l) => (
                                        <div key={l.id} className="flex items-baseline justify-between text-sm">
                                            <span className="text-espresso">{l.name}</span>
                                            <span className="text-[#7A726D] text-xs">
                                                {l.qty_remaining} / {l.qty_per_use} left · {fmtR(l.retail_value)} ea
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {v.restrictions && (
                            <div className="border-l-2 border-[#C48464] pl-3">
                                <div className="text-xs text-[#7A726D] uppercase">Restrictions</div>
                                <div className="text-sm text-[#3E2723] mt-1">{v.restrictions}</div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="mt-8">
                <h2 className="font-display text-xl font-bold text-espresso mb-3">Redemption history</h2>
                <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                    <CardContent className="p-0">
                        <Table data-testid="redemptions-table">
                            <TableHeader>
                                <TableRow className="bg-[#F9F8F6]">
                                    <TableHead>When</TableHead>
                                    <TableHead>Branch</TableHead>
                                    <TableHead>Receipt</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Items</TableHead>
                                    <TableHead>Source</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.redemptions.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#7A726D] py-8">No redemptions yet</TableCell></TableRow>}
                                {data.redemptions.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell className="text-xs">{r.redeemed_at?.slice(0, 19).replace("T", " ")}</TableCell>
                                        <TableCell>{r.branch_name}</TableCell>
                                        <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                                        <TableCell>{fmtR(r.amount_redeemed)}</TableCell>
                                        <TableCell className="text-xs">
                                            {r.items_detail && r.items_detail.length > 0
                                                ? r.items_detail.map((it) => `${it.qty}× ${it.name}`).join(", ")
                                                : (r.items_redeemed || "—")}
                                        </TableCell>
                                        <TableCell className="capitalize text-xs">{r.source}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-espresso">Edit voucher</DialogTitle>
                    </DialogHeader>
                    {form && (
                        <form onSubmit={onSaveEdit} className="space-y-3" data-testid="edit-voucher-form">
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm mt-1" data-testid="edit-name-input"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <Label className="text-xs uppercase text-[#7A726D]">Expiry</Label>
                                    <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="rounded-sm mt-1" data-testid="edit-expiry-input"/>
                                </div>
                            </div>
                            {form.type === "item" ? (
                                <div className="border border-[#E5E0D8] rounded-sm p-3 bg-[#F9F8F6]">
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs uppercase text-[#7A726D]">Line items</Label>
                                        <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-sm h-7" data-testid="edit-add-line-button">
                                            <Plus className="w-3 h-3 mr-1" /> Add item
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {form.lines.map((line, idx) => (
                                            <div key={idx} className="grid grid-cols-[1fr_70px_90px_30px] gap-2 items-center" data-testid={`edit-line-row-${idx}`}>
                                                <Input
                                                    value={line.name}
                                                    onChange={(e) => updateLine(idx, { name: e.target.value })}
                                                    placeholder="Item name"
                                                    className="rounded-sm h-9"
                                                    data-testid={`edit-line-name-${idx}`}
                                                />
                                                <Input
                                                    type="number" min="1"
                                                    value={line.qty_per_use}
                                                    onChange={(e) => updateLine(idx, { qty_per_use: e.target.value })}
                                                    className="rounded-sm h-9"
                                                    data-testid={`edit-line-qty-${idx}`}
                                                />
                                                <Input
                                                    type="number" min="0" step="0.01"
                                                    value={line.retail_value}
                                                    onChange={(e) => updateLine(idx, { retail_value: e.target.value })}
                                                    className="rounded-sm h-9"
                                                    data-testid={`edit-line-value-${idx}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeLine(idx)}
                                                    disabled={form.lines.length === 1}
                                                    className="text-[#B24A45] disabled:opacity-30 disabled:cursor-not-allowed"
                                                    data-testid={`edit-line-remove-${idx}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-[1fr_70px_90px_30px] gap-2 mt-2 text-[10px] uppercase tracking-wide text-[#7A726D]">
                                        <span>Name</span><span>Qty / use</span><span>Retail R</span><span></span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Label className="text-xs uppercase text-[#7A726D]">Rand value (R)</Label>
                                    <Input
                                        type="number" min="0" step="0.01"
                                        value={form.value_amount}
                                        onChange={(e) => setForm({ ...form, value_amount: e.target.value })}
                                        className="rounded-sm mt-1"
                                        data-testid="edit-value-amount-input"
                                    />
                                </div>
                            )}
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Cost shop</Label>
                                <Select value={form.cost_shop_id} onValueChange={(val) => setForm({ ...form, cost_shop_id: val })}>
                                    <SelectTrigger className="rounded-sm mt-1" data-testid="edit-cost-shop-select"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— None —</SelectItem>
                                        {costShops.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Redeemable at</Label>
                                <BranchMultiSelect
                                    branches={branches}
                                    value={form.allowed_branch_ids}
                                    onChange={(val) => setForm({ ...form, allowed_branch_ids: val })}
                                    testid="edit-branches-select"
                                />
                                <div className="text-xs text-[#7A726D] mt-1">Leave empty to allow all branches.</div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Restrictions</Label>
                                <Textarea value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} className="rounded-sm mt-1" data-testid="edit-restrictions-input"/>
                            </div>
                            <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="edit-submit-button">Save changes</Button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Email this voucher</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Send to</Label>
                            <Input type="email" value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                placeholder="customer@example.com"
                                data-testid="email-to-input" />
                        </div>
                        <div>
                            <Label>Email template</Label>
                            <Select value={emailTplId || "default"}
                                onValueChange={(val) => setEmailTplId(val === "default" ? "" : val)}>
                                <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Use default email template</SelectItem>
                                    {emailTemplates.map(t => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}{t.is_default ? " (current default)" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {v.last_emailed_at && (
                            <p className="text-xs text-[#7A726D]">
                                Last sent to <code>{v.last_emailed_to}</code> on {v.last_emailed_at.slice(0, 19).replace("T", " ")}
                            </p>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
                            <Button onClick={sendEmail} disabled={sendingEmail || !emailTo}
                                className="bg-bc-orange hover:bg-[#D26A03] text-white"
                                data-testid="send-email-btn">
                                <Mail className="w-4 h-4 mr-2" />
                                {sendingEmail ? "Sending…" : "Send email"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-xs text-[#7A726D] uppercase tracking-wide">{label}</div>
            <div className="font-medium text-espresso mt-0.5">{value}</div>
        </div>
    );
}
